import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import env from './env';
import LocalStorageService from '../core/services/LocalStorageService';
import CompensationUnlockService from '../features/employees/services/CompensationUnlockService';

interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let queued: Array<(token: string) => void> = [];

// Erstelle eine Instanz mit stabiler baseURL
const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL || env.API_URL || 'http://127.0.0.1:8000',
});

// 1) Request Interceptor: Auth + Tenant + Accept setzen
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // headers-Objekt sicherstellen
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const headers = (config.headers ??= {} as any);

    const token: string | null = LocalStorageService.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // Priority: User-selected tenant from localStorage, fallback to env default
    const tenantId = LocalStorageService.getSelectedTenantId() || env.TENANT_ID;
    if (tenantId) {
      headers['Tenant-ID'] = tenantId;
      headers['X-Tenant-Id'] = tenantId;
    }

    if (config.url && config.url.includes('/compensation')) {
      const compensationTicket = CompensationUnlockService.getTicket();
      if (compensationTicket) headers['X-Compensation-Access'] = compensationTicket;
    }

    headers['Accept'] = 'application/json';

    // Debug logging (temporary - remove after verification)
    const hasAuth = !!headers['Authorization'];
    const hasTenant = !!headers['Tenant-ID'];
    // eslint-disable-next-line no-console

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

// 2) Response Interceptor: Bei 401 => versuche Token-Refresh (Refresh-Once-Pattern)
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response; // normaler Durchlauf: kein Fehler
  },
  async (error: AxiosError): Promise<AxiosResponse | AxiosError> => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 423 - Compensation locked
    if (error.response?.status === 423) {
      const errorData = error.response?.data as any;
      if (errorData?.error === 'compensation_locked' || originalRequest.url?.includes('/compensation')) {
        const enhancedError = error as any;
        enhancedError.isCompensationLocked = true;
        return Promise.reject(enhancedError);
      }
    }

    // Handle 402 Payment Required (dedicated billing status code)
    // Only 402 triggers billing redirect — regular 403s are handled by feature error handlers
    const isBillingApi = originalRequest.url?.includes('/billing/');
    if (!isBillingApi && error.response?.status === 402) {
      const errorData = error.response?.data as any;
      const enhancedError = error as any;
      enhancedError.isBillingError = true;
      enhancedError.billingErrorCode = errorData?.error || 'subscription_inactive';

      const billingMessages: Record<string, string> = {
        quota_exceeded: 'Limit erreicht — bitte Paket upgraden.',
        feature_not_enabled: 'Modul nicht freigeschaltet.',
        subscription_inactive: 'Abo inaktiv — bitte Abo aktivieren.',
      };
      const msg = billingMessages[enhancedError.billingErrorCode] || 'Billing-Fehler aufgetreten.';
      toast.warning(msg, { autoClose: 6000 });

      if (!window.location.pathname.startsWith('/settings/billing')) {
        setTimeout(() => { window.location.href = '/settings/billing'; }, 1500);
      }

      return Promise.reject(enhancedError);
    }

    // Handle 401 - Unauthorized (token expired)
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queued.push((token: string) => {
          if (!token) {
            return reject(error);
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const hdrs = (originalRequest.headers ??= {} as any);
          hdrs['Authorization'] = `Bearer ${token}`;
          const tenantId = LocalStorageService.getSelectedTenantId() || env.TENANT_ID;
          if (tenantId) {
            hdrs['Tenant-ID'] = tenantId;
            hdrs['X-Tenant-Id'] = tenantId;
          }
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken: string | null = LocalStorageService.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const refreshResponse: AxiosResponse<TokenRefreshResponse> = await axios.post(
        `${env.API_URL}/api/admin/token/refresh/`,
        { refresh: refreshToken }
      );

      const newAccessToken: string = refreshResponse.data.access;
      LocalStorageService.setAccessToken(newAccessToken);
      if (refreshResponse.data.refresh) {
        LocalStorageService.setRefreshToken(refreshResponse.data.refresh);
      }

      // Update default headers
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

      // Process queued requests
      queued.forEach((callback) => callback(newAccessToken));
      queued = [];

      // Retry original request
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hdrs = (originalRequest.headers ??= {} as any);
      hdrs['Authorization'] = `Bearer ${newAccessToken}`;
      const tenantId = LocalStorageService.getSelectedTenantId() || env.TENANT_ID;
      if (tenantId) {
        hdrs['Tenant-ID'] = tenantId;
        hdrs['X-Tenant-Id'] = tenantId;
      }

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      console.error('[API] Token refresh failed:', refreshError);

      // Fail all queued requests
      queued.forEach((callback) => callback(''));
      queued = [];

      // Clear tokens and redirect to login
      LocalStorageService.removeAccessToken();
      LocalStorageService.removeRefreshToken();
      LocalStorageService.removeSelectedTenantId();

      // Redirect to login
      window.location.href = '/login';

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance; 
